import React, { useCallback, useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import nlp from "compromise";
import plg, { DatesMethods } from "compromise-dates";
import moment from "moment";
import styled from "styled-components";

import { InputMode, InputSize } from "./types";
import { isValidTime } from "../../utils/timeUtils";
import { ComboBox } from "../ComboBox/ComboBox";
import { IOptionItemProps } from "../Select/Option";
import { Time24HourFormat } from "../SelectTime";

nlp.plugin(plg);

export const StyledInputTimeWrapper = styled.div`
  max-width: 176px;
  flex: 1;

  & .c-combo-box--list {
    min-width: 176px;
  }
`;

export interface InputTimeProps {
  mode?: "12" | "24";
  selected?: Time24HourFormat;
  min?: Time24HourFormat;
  max?: Time24HourFormat;
  onChange?: (val: Time24HourFormat) => void;
  className?: string;
  placeholder?: string;
  size?: InputSize;
  step?: 1 | 5 | 15 | 30 | 60;
  triggerMode?: InputMode;
  disabled?: boolean;
  invalid?: boolean;
  enableVirtualization?: boolean;
  addCustomTimeLabel?: string;
}

const renderOption = (option) => {
  return (
    <div id={option.id} data-testid={`time-option-${option.id}`}>
      {option.name}
    </div>
  );
};

export const InputTime: React.FC<InputTimeProps> = ({
  mode = "24",
  selected: defaultSelected,
  min = "00:00",
  max = "23:45",
  onChange,
  step = 15,
  size,
  className,
  placeholder,
  triggerMode,
  disabled,
  invalid,
  enableVirtualization = false,
  addCustomTimeLabel = "Add",
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const generateTimeOptions = () => {
    const allOptions: IOptionItemProps[] = [];
    const currentTime = moment("00:00", "HH:mm");
    while (currentTime.isBefore(moment("24:00", "HH:mm"))) {
      allOptions.push({
        id: currentTime.format("HH:mm"),
        name: currentTime.format(mode === "12" ? "hh:mm A" : "HH:mm"),
      });
      currentTime.add(step, "minutes");
    }
    return allOptions;
  };
  const timeOptionsFilter = (
    options: IOptionItemProps[],
    _min: Time24HourFormat,
    _max: Time24HourFormat
  ) => {
    const minTime = moment(isValidTime(_min) ? _min : "00:00", "HH:mm");
    const maxTime = moment(isValidTime(_max) ? _max : "23:45", "HH:mm");
    return options.filter(
      (option) =>
        moment(option.id, "HH:mm").isSameOrAfter(minTime) &&
        moment(option.id, "HH:mm").isSameOrBefore(maxTime)
    );
  };
  const [allTimeOptions, setAllTimeOptions] = useState(generateTimeOptions());
  const timeOptions = useMemo(
    () => timeOptionsFilter(allTimeOptions, min, max),
    [allTimeOptions, min, max]
  );

  const isInOptions = useCallback(
    (option) => timeOptions.some((opt) => opt.id === option),
    [timeOptions]
  );

  const isValidDefaultSelected = defaultSelected
    ? isInOptions(defaultSelected)
    : false;

  const [selected, setSelected] = useState<Time24HourFormat | undefined>(
    isValidDefaultSelected ? defaultSelected : undefined
  );

  const handleInputChange = useCallback(
    (text: string) => {
      setSearch(text);
      if (/\d{1,2}:\d{2}/.test(text.trim())) {
        return;
      }
      const doc = nlp<DatesMethods>(text);
      const parsed = doc.times().get()[0];
      if (parsed && parsed["24h"] && parsed["24h"] !== selected) {
        const value = moment(parsed).format("HH:mm") as Time24HourFormat;
        if (timeOptions.some((option) => option.id === value)) {
          setSelected(value);
          onChange?.(value);
          setIsOpen(false);
        }
      }
    },
    [onChange, selected, timeOptions]
  );

  const handleEmptyAction = (newTime: string | number) => {
    const time = String(newTime);
    if (isValidTime(time)) {
      const newOption = {
        id: time,
        name: mode === "12" ? moment(time, "HH:mm").format("hh:mm A") : time,
      };

      const newOptions = [...timeOptions];
      const index = newOptions.findIndex(
        (option) => moment(option.id, "HH:mm") > moment(time, "HH:mm")
      );

      if (index === -1) {
        newOptions.push(newOption);
      } else {
        newOptions.splice(index, 0, newOption);
      }

      setAllTimeOptions(newOptions);
      onChange?.(time as Time24HourFormat);
      setSelected(time as Time24HourFormat);
    }
  };

  const handleChange = (e) => {
    setSelected(e);
    onChange?.(e);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && selected) {
      const selectedItem = document.getElementById(selected)?.parentElement;
      selectedItem?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isOpen, selected]);

  useEffect(() => {
    if (
      defaultSelected !== selected &&
      timeOptions.some((opt) => opt.id === defaultSelected)
    )
      setSelected(defaultSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelected, timeOptions]);

  const isValid = (time?: string) => !isInOptions(time) && isValidTime(time);

  return (
    <StyledInputTimeWrapper className={classNames("c-input-time", className)}>
      <ComboBox
        {...rest}
        options={timeOptions}
        open={isOpen}
        disabledInternalSort
        selected={selected}
        keepSameOptionsOrder
        onInputChange={handleInputChange}
        onChange={handleChange}
        handleEmptyAction={handleEmptyAction}
        emptyValue={
          isValid(search) ? `${addCustomTimeLabel} ${search}` : undefined
        }
        forceCloseMenu
        placeholder={placeholder}
        renderOption={renderOption}
        autoHeightMax={226}
        size={size}
        onClose={handleClose}
        onOpen={handleOpen}
        triggerMode={triggerMode}
        disabled={disabled}
        invalid={invalid}
        disableVirtualization={!enableVirtualization}
      />
    </StyledInputTimeWrapper>
  );
};
